import { Badge, ManageableItemsList } from '@payaca/ambrosia';
import { FC, useCallback } from 'react';

import {
  Automation,
  AutomationAction,
  AutomationActionTypes,
  AutomationCondition,
  AutomationEntityTypes,
} from '@payaca/types/automationTypes';
import { SortDirection } from '@payaca/types/listViewTypes';
import './AutomationsTable.sass';

import useGetMyAccountPipelines from '@/api/queries/me/useGetMyAccountPipelines';
import usePreferredDateFormat from '@/hooks/usePreferredDateFormat';
import {
  conditionValueLabelMap,
  fieldOptionEntityMap,
} from '@/ui/pages/automationsPageOld/automation.utils';

import ResponsiveViewWrapper from '@payaca/components/responsiveViewWrapper/ResponsiveViewWrapper';
import {
  automationEntityTypeReadableNameMap,
  automationTriggerEventReadableNameMap,
} from '@payaca/helpers/automationHelper';
import { DateFormats } from '@payaca/helpers/internationalHelper';
import UntitledIcon from '@payaca/untitled-icons';
import { format } from 'date-fns';

type AutomationRowType = Pick<
  Automation<AutomationActionTypes>,
  'id' | 'title' | 'entityType' | 'triggerEvent' | 'isActive' | 'updatedAt'
> & {
  actions: Pick<AutomationAction<AutomationActionTypes>, 'title'>[];
  conditions: Pick<
    AutomationCondition,
    'field' | 'operator' | 'value' | 'isActive'
  >[];
};

type Props = {
  automations: AutomationRowType[];
  onClickRow?: (automationId: AutomationRowType['id']) => void;
  quickActionDefinitions?: {
    actionName: string;
    actionBehaviour: (id: number) => void;
    isActionProcessing?: boolean;
  }[];
  isLoading?: boolean;
  sortBy?: string;
  sortDirection?: SortDirection;
  onSort?: (field: string, sortDirection: SortDirection) => void;
};

const AutomationsTable: FC<Props> = ({
  automations,
  onClickRow,
  quickActionDefinitions,
  isLoading,
  sortBy,
  sortDirection,
  onSort,
}) => {
  const { data: pipelines = [] } = useGetMyAccountPipelines();

  const preferredDateFormat = usePreferredDateFormat(DateFormats.TIMESTAMP);

  const getConditionAsString = useCallback(
    (
      condition: Pick<AutomationCondition, 'value' | 'operator' | 'field'>,
      entityType: AutomationEntityTypes
    ) => {
      let conditionValue: string | undefined = condition.value;

      switch (condition.field) {
        case 'deal.pipelineId':
          conditionValue = pipelines.find(
            (x) => x.id == condition.value
          )?.title;
          break;
      }

      const [dataAccessType, arg] = condition.field.split(':');
      const label = fieldOptionEntityMap?.[entityType]?.[dataAccessType]?.label;

      return `"${
        label ? `${label}${arg ? `: ${arg}` : ''}` : 'Missing field'
      }" ${conditionValueLabelMap[condition.operator] || 'Missing operator'} "${
        conditionValue || 'Missing value'
      }"`;
    },
    [pipelines]
  );

  return (
    <ResponsiveViewWrapper className="automations-table" downBreakpointSm={800}>
      <ManageableItemsList.Table
        uniqueKey="id"
        emptyText="No automations found"
        items={automations}
        itemActions={quickActionDefinitions?.map((qad) => {
          return {
            label: qad.actionName,
            onClick: (row: AutomationRowType) => qad.actionBehaviour(row.id),
            isProcessing: qad.isActionProcessing,
          };
        })}
        isLoading={isLoading}
        onClickRow={(row) => {
          onClickRow?.(row.id);
        }}
        sortBy={sortBy as keyof AutomationRowType}
        sortDirection={sortDirection}
      >
        <ManageableItemsList.Table.Column<AutomationRowType, 'title'>
          header="Name"
          field={'title'}
          onSort={onSort}
          render={(title) => {
            return <strong>{title}</strong>;
          }}
        />
        <ManageableItemsList.Table.Column<AutomationRowType, 'entityType'>
          header="Type"
          field={'entityType'}
          onSort={onSort}
          render={(entityType) => {
            return (
              <>
                {automationEntityTypeReadableNameMap[entityType] || entityType}
              </>
            );
          }}
        />
        <ManageableItemsList.Table.Column<AutomationRowType, 'triggerEvent'>
          header="Trigger event"
          field={'triggerEvent'}
          onSort={onSort}
          render={(triggerEvent) => {
            return (
              <>
                {automationTriggerEventReadableNameMap[triggerEvent] ||
                  triggerEvent}
              </>
            );
          }}
        />
        <ManageableItemsList.Table.Column<AutomationRowType, 'conditions'>
          header="Conditions"
          className="hide-sm"
          field={'conditions'}
          render={(conditions, automation) => {
            return (
              <>
                {!conditions.length ? (
                  <>None</>
                ) : (
                  <ul>
                    {conditions
                      .filter((x) => x.isActive)
                      .map((x, i) => {
                        return (
                          <li key={i}>
                            {getConditionAsString(x, automation.entityType)}
                          </li>
                        );
                      })}
                  </ul>
                )}
              </>
            );
          }}
        />
        <ManageableItemsList.Table.Column<AutomationRowType, 'actions'>
          header="Actions"
          className="hide-sm"
          field={'actions'}
          render={(actions) => {
            return (
              <>
                {!actions.length ? (
                  <>None</>
                ) : (
                  <ul>
                    {actions.map((x, i) => {
                      return <li key={i}>{x.title}</li>;
                    })}
                  </ul>
                )}
              </>
            );
          }}
        />
        <ManageableItemsList.Table.Column<AutomationRowType, 'updatedAt'>
          header="Updated at"
          field="updatedAt"
          render={(updatedAt) => {
            return format(updatedAt, preferredDateFormat);
          }}
          onSort={onSort}
        />
        <ManageableItemsList.Table.Column<AutomationRowType, 'isActive'>
          header="On/Off"
          field={'isActive'}
          render={(isActive) => {
            if (isActive) {
              return (
                <Badge variant="soft" colour="green">
                  <UntitledIcon name="check" className="w-4 h-4" />
                  Active
                </Badge>
              );
            } else {
              return <Badge variant="soft">Inactive</Badge>;
            }
          }}
        />
      </ManageableItemsList.Table>
    </ResponsiveViewWrapper>
  );
};

export default AutomationsTable;
